import InteractiveElement, { withRef, isIntersectingCamera } from '../common/IE_HOC'
import compose from 'recompose/compose'
import branch from 'recompose/branch'
import renderComponent from 'recompose/renderComponent'
import Hotspot2D from '../../components/2d/hotspot'
import { SpriteSheetHotspot3D } from '../../components/3d/hotspot3D'
import React from 'react'
import { lookupElement } from '../../methods/lookupElement'
import {
    hotspotClicked,
    setHotspotCoordinates,
    hotspotMouseEntered,
    hotspotMediaViewed,
} from '../../actions/hotspotActions'
import { openPreview, openModal, forceMediaPause } from '../../actions/common.js'
import { connect } from 'react-redux'
import { playAudio, AUDIO_HS } from 'aria-viewer/audiomanager/audioManagerActions'
import { INTERACTIVE_ELEMENT_OPTIONS } from '../../constants'
import { setCursor } from 'aria-viewer/players/panoplayer/actions/panoramaActions'
import { Capabilities, displayFeatureBasedOnViewportSize } from 'aria-viewer/utils/capabilities'

const withType = Component => props => <Component {...props} type="hotspots" />

const addProps = Component => ({
    state,
    isUserDraggingPano,
    isPanoAutoRotating,
    visitedStop,
    index,
    cartesian3DCoords,
    forwardRef,
    contextType,
    ...props
}) => {
    const mediaItem = lookupElement({ type: 'mediaItems', id: props.mediaItems[0], state, toJS: true })
    const { item_type: type } = mediaItem
    const isPreviewOpen = state.getIn(['interactiveElements', 'preview', 'isOpen'])
    const start_fov = state.getIn(['interactiveElements', 'currentScene', 'scene', 'start_fov'])
    let isVisited = false
    let currentState = 'default'
    const seed = index % 5
    const isMobile = state.getIn(['device', 'isDeviceMobile'])

    if (visitedStop) {
        isVisited = !!visitedStop.find(({ id: vId, type: vType }) => props.id === vId && vType === 'hotspots')
    }

    if (isVisited) {
        currentState = 'visited'
    } else if (
        contextType !== INTERACTIVE_ELEMENT_OPTIONS.CONTEXT.context2D &&
        (isPanoAutoRotating || isUserDraggingPano)
    ) {
        currentState = 'panning'
    }

    // TODO: We should use something more common across the board ie position
    const meshPosition = {
        ...cartesian3DCoords,
    }

    // Determine Hotspot Visibility
    const isIntersecting = isIntersectingCamera(forwardRef.current)

    return (
        <Component
            {...{
                ...props,
                type,
                mediaItem,
                isIntersecting,
                currentState,
                isPreviewOpen,
                seed,
                isMobile,
                fov: start_fov ? start_fov : 100,
                meshPosition,
                contextType,
                forwardRef,
            }}
        />
    )
}

const mapDispatchToProps = dispatch => ({
    setCursor: cursor => {
        dispatch(setCursor(cursor))
    },

    openPreview: ({ id, type, previewType }) => {
        dispatch(openPreview({ id, type, previewType }))
    },
    registerAudio: () => {
        dispatch(playAudio(AUDIO_HS))
    },
    hotspotClicked: ({ id, type, mediaId, mediaType }) => {
        dispatch(hotspotClicked())
        dispatch(openModal({ id, type, mediaId, mediaType }))
    },
    openModal: ({ id, type, mediaId, mediaType }) => {
        dispatch(openModal({ id, type, mediaId, mediaType }))
    },
    setForcePause: pause => {
        dispatch(forceMediaPause(pause))
    },
    hotspotMouseEntered: () => {
        dispatch(hotspotMouseEntered())
    },
    hotspotMediaViewed: hotspotType => {
        dispatch(hotspotMediaViewed({ hotspotType }))
    },
    setHotspotCoordinates: hotspotCoordinates => {
        dispatch(setHotspotCoordinates(hotspotCoordinates))
    },
})

const mergeProps = (state, actions, ownProps) => ({
    ...state,
    ...actions,
    ...ownProps,
    onClick: e => {
        const {
            hotspotClicked,
            hotspotMediaViewed,
            openModal,
            registerAudio,
            setForcePause,
            setHotspotCoordinates,
        } = actions
        const { id, type, mediaId, mediaType, mediaItems } = ownProps
        const clientX = e.clientX
        const clientY = e.clientY
        const hotspotCoordinates = { x: clientX, y: clientY }

        setHotspotCoordinates(hotspotCoordinates)

        hotspotClicked({ id, type, mediaId, mediaType })
        hotspotMediaViewed(type)
        registerAudio()
        setForcePause(true)
        openModal({
            id,
            mediaType: type,
            mediaId: mediaItems[0],
            type: 'hotspots',
        })
    },
    onMouseEnter: e => {
        const { openPreview, hotspotMouseEntered } = actions
        const { hasPreview } = state
        const { id, type, isPreviewOpen, setAutoRotate } = ownProps

        if (!isPreviewOpen && hasPreview) {
            hotspotMouseEntered()
            setAutoRotate && setAutoRotate(false)
            openPreview({
                id,
                previewType: type,
                type: 'hotspots',
            })
        }
    },
})

const mapStateToProps = state => {
    const viewportWidth = state.getIn(['device', 'viewportWidth'])
    const viewportHeight = state.getIn(['device', 'viewportHeight'])
    const hasPreview = displayFeatureBasedOnViewportSize(Capabilities.HOTSPOT_PREVIEW, {
        viewportWidth,
        viewportHeight,
    })
    return {
        hasPreview,
    }
}

export default compose(
    withType,
    withRef,
    InteractiveElement,
    addProps,
    connect(mapStateToProps, mapDispatchToProps, mergeProps),
    branch(
        ({ enable3D, contextType }) => enable3D && contextType !== INTERACTIVE_ELEMENT_OPTIONS.CONTEXT.context2D,
        renderComponent(SpriteSheetHotspot3D),
        renderComponent(Hotspot2D)
    )
)()
